<template>
  <div class="suitSelectComponent">
    <b-card no-body>
      <b-card-header class="d-flex align-items-center justify-content-between">
        <div>Suit Selection</div>
        <div><b-button variant="outline-dark" size="sm" @click="reset">reset</b-button></div>
      </b-card-header>

      <b-card-body v-if="isAvailable" class="d-flex">
        <div>
          <div class="label">All suit options:</div>
          <div class="fullComboSelect">
            <div v-for="(row, i) in fullSelect" :key="i" class="d-flex">
              <div
                class="comboContainer"
                v-for="comboData in row"
                :key="comboData.comboString"
                :class="{ active: comboData.selected, disabled: comboData.disabled }"
              >
                <div
                  class="combo"
                  :style="comboData.style"
                  @mousedown="e => mousedown(e, comboData)"
                  @mouseup="e => mouseup(e, comboData)"
                  @mouseenter="e => mouseenter(e, comboData)"
                >
                  <Suit
                    v-for="(suit, i) in comboData.comboString.split('')"
                    :key="i"
                    :suit="suit"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="alternateSubrangeSelect mt-3">
            <div class="label">Action for all other suits:</div>
            <b-form-select
              :value="this.alternateSubrangeIndex"
              :options="alternateSubrangeSelectOptions"
              @input="value => $emit('changeAlternateSubrangeIndex', value)"
            />
          </div>
        </div>

        <div class="quickComboSelect ml-3">
          <div class="label">Quick select:</div>
          <b-list-group>
            <b-list-group-item
              v-for="suit in quickSelectableSuits"
              :key="suit"
              class="quickSelectListItem"
            >
              <div class="suit">
                <div class="checkboxContainer mr-4">
                  <b-form-checkbox
                    v-model="checkboxes[suit].any"
                    @change="value => toggleCheckbox(value, suit, 'any')"
                  >
                    <div class="d-flex align-items-start justify-content-end">
                      at least one <Suit class="ml-2" :suit="suit" />
                    </div>
                  </b-form-checkbox>
                </div>
                <div class="checkboxContainer">
                  <b-form-checkbox
                    v-model="checkboxes[suit].high"
                    @change="value => toggleCheckbox(value, suit, 'high')"
                  >
                    <div class="d-flex align-items-start justify-content-end">
                      high card is a <Suit class="ml-2" :suit="suit" />
                    </div>
                  </b-form-checkbox>
                </div>
              </div>
            </b-list-group-item>
          </b-list-group>
        </div>
      </b-card-body>

      <b-card-body v-else>
        only available postflop
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import Vue from "vue";
import countBy from "lodash/countBy";
import chunk from "lodash/chunk";
import {
  cardsToSuits,
  allSuitComboStrings,
  suitComboStringsToSuitQuickSelection,
  suitQuickSelectionToSuitComboStrings,
} from "../helpers/cards";
import Suit from "./Suit";

export default {
  name: "SuitSelect",
  components: { Suit },
  props: {
    // v-model value: selected suit combos
    value: { type: Array, required: true },
    subranges: { type: Array, required: true },
    colorScheme: { type: Array, required: true },
    activeSubrangeIndex: { type: Number, required: true },
    alternateSubrangeIndex: { type: Number, required: true },
    board: { type: Array, required: true },
    highlightSuits: { type: Object, default: null },
  },
  data() {
    return {
      showAllQuickSelectOptions: false,
      checkboxes: suitComboStringsToSuitQuickSelection(this.value),
      isPainting: false,
    };
  },
  computed: {
    isAvailable() {
      return this.board.length >= 3;
    },
    quickSelectableSuits() {
      // The only suits relevant for quick selection are the ones that can make flushes,
      // so they have to be present on the flop
      const suits = cardsToSuits(this.board);
      const suitCounts = countBy(suits);
      const sortBySuitCount = (a, b) => suitCounts[b] - suitCounts[a];
      // Unique flop suits sorted (desc) by total number of cards of the suit on the board
      const flopSuits = [...new Set(cardsToSuits(this.board.slice(0, 3)))].sort(sortBySuitCount);
      const otherSuits = [...new Set(suits.filter(suit => !flopSuits.includes(suit)))].sort(
        sortBySuitCount
      );
      // Show all suits if the all suits option is set, else only the flop suits
      const quickSelectableSuits = !this.showAllQuickSelectOptions
        ? flopSuits
        : [...flopSuits, ...otherSuits];
      // If any other checkboxes happen to be selected because they match whatever the user
      // selected manually in the suit grid, show those too
      for (let [suit, checks] of Object.entries(this.checkboxes)) {
        if (!Object.values(checks).includes(true)) continue;
        quickSelectableSuits.push(suit);
      }
      const uniqueQuickSelectableSuits = [...new Set(quickSelectableSuits)];
      return uniqueQuickSelectableSuits;
    },
    hasHighlightedSuits() {
      return !!this.highlightSuits;
    },
    fullSelect() {
      const suitComboData = allSuitComboStrings.map(comboString => {
        const isHighlighted =
          this.hasHighlightedSuits && this.highlightSuits[comboString] !== undefined;
        return {
          comboString,
          selected: this.value.includes(comboString),
          highlighted: isHighlighted,
          style: !isHighlighted
            ? {}
            : { background: this.colorScheme[this.highlightSuits[comboString]] },
          disabled: this.hasHighlightedSuits && !isHighlighted,
        };
      });
      return chunk(suitComboData, 4);
    },
    alternateSubrangeSelectOptions() {
      const defaultOption = { value: -1, text: "don't change" };
      const subrangeOptions = this.subranges
        .map((data, i) => ({ value: i, text: data.label }))
        .filter(options => options.value !== this.activeSubrangeIndex);
      return [defaultOption, ...subrangeOptions];
    },
  },
  methods: {
    reset() {
      this.$emit("input", []);
    },
    toggleCheckbox(value, suit, type) {
      // Only one option per suit can be selected
      if (value === true) {
        type === "any" ? (this.checkboxes[suit].high = false) : (this.checkboxes[suit].any = false);
      }
      Vue.nextTick(() => {
        this.updateSelectedCombosFromCheckboxes();
      });
    },
    toggleCombo(comboString, setToActive) {
      if (setToActive) {
        this.$emit("input", [...this.value, comboString]);
      } else {
        const comboStrings = this.value.filter(_comboString => _comboString !== comboString);
        this.$emit("input", comboStrings);
      }
    },
    updateSelectedCombosFromCheckboxes() {
      this.$emit("input", suitQuickSelectionToSuitComboStrings(this.checkboxes));
    },
    mousedown(e, comboData) {
      if (e.buttons !== 1) return;
      this.isPainting = true;
      this.mouseenter(e, comboData);
    },
    mouseup(e) {
      if (e.buttons !== 1) return;
      this.isPainting = false;
    },
    mouseenter(e, comboData) {
      if (e.buttons !== 1) return;
      this.toggleCombo(comboData.comboString, !comboData.selected);
    },
  },
  watch: {
    board(board) {
      if (board.length < 3) {
        this.$emit("input", []);
      }
    },
    value(combos) {
      this.checkboxes = suitComboStringsToSuitQuickSelection(combos);
    },
    // // When the board changes, remove any previous suit selections that don't match the current board
    // quickSelectableSuits(suits) {
    //   for (const suit of Object.keys(this.checkboxes)) {
    //     if (!suits.includes(suit)) {
    //       this.checkboxes[suit] = { any: false, high: false };
    //     }
    //   }
    //   this.updateSelectedCombosFromCheckboxes();
    // },
  },
};
</script>

<style lang="scss" scoped>
.label {
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
  margin-right: 10px;
  margin-bottom: 7px;
}
.comboContainer {
  display: flex;
  border: 1px solid #444;
  position: relative;
  top: 0;
  left: 0;
  opacity: 1;
  background: white;
  transition: top, left, border-color, box-shadow;
  transition-duration: 0.1s;
  &.disabled {
    border-color: #eee;
    .combo {
      opacity: 0.1;
    }
  }
  &.active {
    top: -3px;
    left: -3px;
    box-shadow: 3px 3px 3px 1px rgba(0, 0, 0, 1);
    border-color: black;
    .combo {
      background: bisque;
    }
  }
}
.combo {
  position: relative;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 5px;
  width: 50px;
  height: 30px;
  cursor: pointer;
  transition: opacity, background-color;
  transition-duration: 0.1s;
}
.quickComboSelect {
  font-size: 14px;
}
.quickSelectListItem {
  padding: 0.35rem 1rem;
}
</style>
